import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, IconButton, Button, Drawer, CircularProgress ,Dialog as MuiDialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadIcon from "@mui/icons-material/Upload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ImageIcon from "@mui/icons-material/Image";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import DescriptionIcon from "@mui/icons-material/Description";
import FetchFiles from "../../../utils/storage/FetchFiles";
import UploadFiles from "../../../utils/storage/UploadFiles";
import Loader from "../../general/Loader";
import { toast } from "react-toastify";
import "./FileUploadPopup.css";
import { CancelOutlined as CancelIcon } from "@mui/icons-material";
import { useDropzone } from "react-dropzone";
import { Dialog } from "primereact/dialog";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteFiles from "../../../utils/storage/DeleteFiles";


const FileUploadPopup = ({ visible, onHide, extensionId, handleFileChangeFromSidebar, setSelectedDocumentIds, }) => {
    const [existingFiles, setExistingFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedForUpload, setSelectedForUpload] = useState([]);
    const [uploadStatus, setUploadStatus] = useState({});
    const [uploading, setUploading] = useState(false);
    const [open, setOpen] = useState(false);
    const [fileToDelete, setFileToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        if (visible && extensionId) {
            setLoading(true);
            FetchFiles("default", extensionId)
                .then(files => setExistingFiles(Array.from(new Map((files || []).map(file => [file.file_name, file])).values())))
                .catch(error => {
                    console.error("Failed to load files:", error);
                    setExistingFiles([]);
                })
                .finally(() => setLoading(false));
        }
    }, [visible, extensionId]);

    const onDrop = useCallback((acceptedFiles) => {
        setSelectedForUpload((prevFiles) => [...prevFiles, ...acceptedFiles]);
    }, []);

    const MAX_TOTAL_FILE_SIZE = parseFloat(process.env.REACT_APP_MAX_TOTAL_FILE_SIZE || '100');
    const MAX_TOTAL_FILE_SIZE_UNIT = process.env.REACT_APP_MAX_TOTAL_FILE_SIZE_UNIT || 'MB';

    let maxSizeInBytes;
    if (MAX_TOTAL_FILE_SIZE_UNIT.toUpperCase() === 'GB') {
        maxSizeInBytes = MAX_TOTAL_FILE_SIZE * 1024 * 1024 * 1024;
    } else if (MAX_TOTAL_FILE_SIZE_UNIT.toUpperCase() === 'MB') {
        maxSizeInBytes = MAX_TOTAL_FILE_SIZE * 1024 * 1024;
    } else if (MAX_TOTAL_FILE_SIZE_UNIT.toUpperCase() === 'KB') {
        maxSizeInBytes = MAX_TOTAL_FILE_SIZE * 1024;
    } else {
        maxSizeInBytes = MAX_TOTAL_FILE_SIZE * 1024 * 1024; // Default is MB
    }

    const { getRootProps, getInputProps } = useDropzone({
        onDrop: (acceptedFiles) => {
            const totalFileSize = [...selectedForUpload, ...acceptedFiles].reduce((total, file) => total + file.size, 0);

            if (totalFileSize > maxSizeInBytes) {
                toast.error(`Total file size exceeds ${MAX_TOTAL_FILE_SIZE} ${MAX_TOTAL_FILE_SIZE_UNIT} and was not selected.`);
                return;
            }

            setSelectedForUpload((prevFiles) => [...prevFiles, ...acceptedFiles]);
        },
        multiple: true,
        accept: {
            "image/*": [], // jpg, jpeg, png, gif, bmp, tiff, svg, webp, ico, heif, heic, etc.
            "video/*": [], // mp4, avi, mov, wmv, flv, mkv, webm, mpeg, mpg, 3gp, etc.
            "application/pdf": [], // pdf
            "application/msword": [], // doc
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [], // docx
            "text/plain": [], // txt, log
            "application/vnd.ms-excel": [], // xls (older Excel format)
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [], // xlsx (modern Excel format)
            "text/csv": [] // csv
        }
        ,
        disabled: uploading,
    });


    const handlePreviouslyUploadedClick = (file) => {
        handleFileChangeFromSidebar?.({
            name: file.file_name,
            uri: file.file_uri,
            documentId: file.metadata?.documentId,
        });
        setSelectedDocumentIds(prevIds => [...prevIds, file.metadata?.documentId]);
    };

    const handleClearUploadFile = (fileToRemove) => {
        setSelectedForUpload(prevFiles => prevFiles.filter(file => file.name !== fileToRemove.name));
    };

    const getFileIcon = (fileName) => {
        const extension = fileName.split(".").pop().toLowerCase();
        const icons = {
            jpg: ImageIcon, jpeg: ImageIcon, png: ImageIcon, gif: ImageIcon,
            mp4: VideoLibraryIcon, mov: VideoLibraryIcon, avi: VideoLibraryIcon,
            pdf: DescriptionIcon, doc: DescriptionIcon, docx: DescriptionIcon, txt: DescriptionIcon
        };
        const IconComponent = icons[extension] || InsertDriveFileIcon;
        return <IconComponent className="file-type-icon" />;
    };

    // Handle Delete File
    const deleteFile = async () => {
        if (!fileToDelete) return;

        const { chatId, metadata, file_name } = fileToDelete;
        const documentId = metadata?.documentId;
        setDeleting(true);
        try {
            const response = await DeleteFiles(chatId, documentId);
            
            if (response) {
                toast.success(`${file_name} deleted successfully`);
                setExistingFiles((prevFiles) => prevFiles.filter(file => file !== fileToDelete));
            } else {
                toast.error("An error occurred while deleting the file");
            }
        } catch (error) {
            setDeleting(false);
            console.error("Error deleting file:", error);
            toast.error("An error occurred while deleting the file");
        } finally {
            setDeleting(false);
            handleClose();
        }
    };
    const handleUploadFiles = async () => {
        const fileCount = selectedForUpload.length;
        setUploadStatus(prev => Object.fromEntries(selectedForUpload.map(file => [file.name, "uploading"])));
        setUploading(true);
        const locationType = "extension";


        try {
            const res = await UploadFiles(selectedForUpload, "default", (fileName, status) => {
                setUploadStatus(prev => ({ ...prev, [fileName]: status === "uploaded" ? "uploaded" : "error" }));
            }, extensionId);

            const uploadedfiles = res?.uploadedFiles;

            if (Array.isArray(uploadedfiles)) {
                setExistingFiles((prevFiles) => {
                    const fileMap = new Map(prevFiles.map(file => [file.file_name, file])); // Preserve existing files
                    uploadedfiles.forEach(file => fileMap.set(file.file_name, file)); // Append new files
                    return Array.from(fileMap.values());
                });
            }

            // Show different toast messages based on the number of files uploaded
            if (fileCount === 1) {
                toast.success(`${selectedForUpload[0].name} uploaded successfully!`);
            } else {
                toast.success(`${fileCount} files uploaded successfully!`);
            }

            setSelectedForUpload([]);
        } catch (error) {
            console.error("Error during file upload:", error);
        } finally {
            setUploading(false);
        }
    };
    const handleOpen = (file) => {
        setFileToDelete(file);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setFileToDelete(null);
    };



    return (
        <Dialog header="Upload Files" visible={visible} style={{ width: '30vw' }} onHide={onHide} >
            <MuiDialog open={open} onClose={handleClose}>
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete <strong>{fileToDelete?.file_name}</strong>?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="primary">Cancel</Button>
                    <Button
                        onClick={deleteFile}
                        color="error"
                        autoFocus
                        disabled={deleting}
                        startIcon={deleting ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                        {deleting ? "Deleting..." : "Delete"}
                    </Button>
                </DialogActions>
            </MuiDialog>
            <Box className="file-upload-popup">


                <Box className="section">
                    <Box className="section-header">
                        <Typography variant="subtitle2" fontWeight="bold">Previously Uploaded Files</Typography>
                    </Box>
                    <Box className="section-body">
                        {loading ? (
                            <Loader />
                        ) : existingFiles.length > 0 ? (
                            existingFiles.map((file, index) => (
                                <Box
                                    key={index}
                                    className="fileCard"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                >
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        onClick={() => handlePreviouslyUploadedClick(file)}
                                        style={{ cursor: "pointer" }}
                                    >
                                        {getFileIcon(file.file_name)}
                                        <Typography variant="body2" title={file.file_name}>
                                            {file.file_name.length > 15
                                                ? file.file_name.substring(0, 12) + "..."
                                                : file.file_name}
                                        </Typography>
                                    </Box>
                                    <div className="fileAction">
                                        <IconButton
                                            size="small"
                                            onClick={() => window.open(file.file_uri, "_blank")}
                                            style={{ color: "#333366" }}
                                        >
                                            <VisibilityIcon fontSize="small" />
                                        </IconButton>

                                        {!uploading && <Button
                                            variant="text"
                                            size="small"
                                            onClick={() => handleOpen(file)}
                                            style={{ color: "#333366" }}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </Button>}
                                    </div>
                                </Box>
                            ))
                        ) : (
                            <Typography variant="body2" color="textSecondary" className="section-body-text">
                                No files have been uploaded for this chat yet.
                            </Typography>
                        )}
                    </Box>
                </Box>

                {/* Files Selected for Upload */}
                <Box className="section">
                    <Box className="section-header">
                        <Typography variant="subtitle2" fontWeight="bold">Files Selected for Upload</Typography>
                    </Box>
                    <Box className="section-body">
                        {selectedForUpload.length > 0 ? (
                            selectedForUpload.map((file, index) => (
                                <Box
                                    key={index}
                                    className={`fileCard ${uploadStatus[file.name] || ""}`}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                >
                                    {getFileIcon(file.name)}
                                    <Typography variant="body2" title={file.name}>
                                        {file.name.length > 15
                                            ? file.name.substring(0, 12) + "..."
                                            : file.name}
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleClearUploadFile(file)}
                                        disabled={uploadStatus[file.name] === "uploading"}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            ))
                        ) : (
                            <Typography variant="body2" color="textSecondary" className="section-body-text">
                                No files selected for upload.
                            </Typography>
                        )}
                    </Box>
                </Box>

                <Box className="file-upload-container">
                    <div {...getRootProps()} className={`dropzone ${uploading ? "disabled" : ""}`}>
                        <input {...getInputProps()} />
                        <Typography>Drag & Drop files here, or click to select</Typography>
                    </div>
                    {selectedForUpload.length > 0 && (
                        <Box display="flex" justifyContent="flex-end" width="100%" mt={2}>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={handleUploadFiles}
                                startIcon={uploading ? <CircularProgress size={14} /> : <UploadIcon />}
                                disabled={uploading}
                                className="upload-button"
                            >
                                {uploading ? "Uploading..." : "Upload"}
                            </Button>
                        </Box>
                    )}
                </Box>





            </Box>
        </Dialog>
    );
};

export default FileUploadPopup;